import { useState, useEffect } from 'react';
import { t } from '../../i18n/i18n';
import UploadTestFiles from '../UploadTestFiles/UploadTestFiles';
import { AnswerTypeEnum, TestFileType, languagesToImplement } from '../../constants/enums';
import GenericToggle from '../GenericToggle/GenericToggle';
import DropdownSelect from '../DropdownSelect/DropdownSelect';
import CodeAnswer from '../CodeAnswer/CodeAnswer';
import ButtonModalTestSuggestionAI from '../ButtonModalTestSuggestionAI/ButtonModalTestSuggestionAI';
import { useQuestionHandlers } from '../../hooks/useQuestionHandlers';

const QuestionCode = ({ testGroupABM = false, readOnly = false, isTranslateABM = false }) => {
  const {
    currentQuestionData,
    handleAddCodeAnswers,
    handleAddQuestionText,
    handleAddTestFile,
    handleRemoveTestFile,
    handleUpdateTestFile,
    defaultCodeOutputState,
    handleAddQuestionLanguage,
    removeLangCodeProperties,
  } = useQuestionHandlers(isTranslateABM);
  const [rowAnswer, setRowAnswer] = useState(defaultCodeOutputState);
  const [hasInitialCode, setHasInitialCode] = useState(true);
  const [hasAICorrection, setHasAICorrection] = useState(true);

  useEffect(() => {
    setRowAnswer(currentQuestionData?.expectedResult ?? defaultCodeOutputState);
  }, [currentQuestionData?.expectedResult]);

  useEffect(() => {
    if (currentQuestionData?.answerType === AnswerTypeEnum.CODE_AI) {
      setHasAICorrection(true);
    }
  }, [currentQuestionData?.answerType, currentQuestionData?.id]);

  useEffect(() => {
    if (!currentQuestionData?.text) {
      const copyRows = [...defaultCodeOutputState];
      setRowAnswer(currentQuestionData?.expectedResult ?? copyRows);
    }
  }, [currentQuestionData]);

  const handleOnChange = (index, key, value) => {
    const copyRows = [...rowAnswer];
    copyRows[index] = {
      ...copyRows[index],
      [key]: value,
    };
    setRowAnswer(copyRows);
    handleAddCodeAnswers(copyRows);
  };

  const handleAddAswer = () => {
    const emptyNewOption = {
      text: '',
      ...(!testGroupABM ? { correct: false } : {}),
    };
    setRowAnswer(rowAnswer.concat(emptyNewOption));
    handleAddCodeAnswers(rowAnswer.concat(emptyNewOption));
  };

  const handleOnRemove = (index) => {
    const copyRows = [...rowAnswer];
    copyRows.splice(index, 1);
    handleAddCodeAnswers(copyRows);
  };

  const resetInitalCode = () => {
    handleAddQuestionText({ target: { name: 'existingCode', value: '' } });
  };

  return (
    <div data-testid="question-code-container">
      {!isTranslateABM && !readOnly && <ButtonModalTestSuggestionAI />}
      <textarea
        type="text"
        onChange={handleAddQuestionText}
        name="text"
        value={currentQuestionData?.text ?? ''}
        className={`w-100 mt-3 py-2 px-3  border-thin-blue-principal outline-none text-xs min-height-add-question ${
          currentQuestionData?.text?.length ? 'selected-border' : 'unselected-border'
        }`}
        rows="3"
        maxLength={5000}
        placeholder={t('CREATE_SCREENING_YOUR_QUESTIONS_CODE_INPUT_PLACEHOLDER')}
        data-testid="question-code-add-question-textarea"
        disabled={readOnly}
      />
      {testGroupABM && (
        <textarea
          type="text"
          onChange={handleAddQuestionText}
          name="formula"
          value={currentQuestionData?.formula ?? ''}
          className={`w-100 mt-3 py-2 px-3  border-thin-blue-principal outline-none text-xs min-height-add-question ${
            currentQuestionData?.formula?.length ? 'selected-border' : 'unselected-border'
          }`}
          rows="1"
          maxLength={500}
          placeholder={t('CREATE_SCREENING_YOUR_FORMULAS_PLACEHOLDER')}
          data-testid="question-mc-add-formula-textarea"
          disabled={readOnly}
        />
      )}
      <div
        className="w-full d-flex justify-content-between"
        data-testid="code-options-language-prexisting-code"
      >
        <GenericToggle
          className="my-3"
          rightText={t('TOGGLE_HAS_INITAL_CODE')}
          defaultActive={hasInitialCode}
          actionFunction={() => {
            resetInitalCode();
            setHasInitialCode(!hasInitialCode);
          }}
          disabled={readOnly}
        />
        <GenericToggle
          className="my-3"
          rightText={t('TOGGLE_HAS_AI_CORRECTION')}
          disabled={!testGroupABM || readOnly}
          isEditTG={!testGroupABM}
          defaultActive={hasAICorrection}
          actionFunction={() => {
            removeLangCodeProperties(
              !hasAICorrection ? AnswerTypeEnum.CODE_AI : AnswerTypeEnum.CODE
            );
            setHasAICorrection(!hasAICorrection);
          }}
        />
        {!hasAICorrection && (
          <span className="align-items-center">
            <DropdownSelect
              selectedPlaceholder
              alignEnd="justify-content-end"
              className="mr-0 mr-2"
              placeholder={t('MULTIPLE_SELECT_CODE_LANGUAGE_PLACEHOLDER')}
              setState={(e) => handleAddQuestionLanguage(e)}
              selected={currentQuestionData?.langCode}
              options={languagesToImplement?.map((item) => ({
                id: item.id,
                name: item.displayName,
                version: item?.version ?? 0,
              }))}
              disabled={readOnly}
            />
            <p
              className={` text-xs fit-content mb-0 font-weight-500 ${
                currentQuestionData?.langCode
                  ? 'px-2 b-radius-8 bg-yellow-warning py-1 w-100 my-1 gray-subtitle'
                  : ''
              }`}
            >
              {currentQuestionData?.langCode ? 'Se utilizará la ultima version del lenguaje' : ''}
            </p>
          </span>
        )}
      </div>
      {hasInitialCode && (
        <textarea
          type="text"
          onChange={handleAddQuestionText}
          name="existingCode"
          value={currentQuestionData?.existingCode ?? ''}
          className={`w-100 mb-3 py-2 px-3  border-thin-blue-principal outline-none text-xs min-height-add-question ${
            currentQuestionData?.existingCode?.length ? 'selected-border' : 'unselected-border'
          }`}
          rows="5"
          maxLength={5000}
          placeholder={t('CREATE_SCREENING_YOUR_QUESTIONS_INITIAL_CODE_INPUT_PLACEHOLDER')}
          data-testid="question-code-add-inital-code-textarea"
          disabled={readOnly}
        />
      )}
      {testGroupABM && !readOnly && (
        <UploadTestFiles
          fileTypes={[TestFileType.IMAGE, TestFileType.AUDIO]}
          handleAddFile={handleAddTestFile}
          handleRemoveFile={handleRemoveTestFile}
          handleUpdateFile={handleUpdateTestFile}
          testFiles={currentQuestionData?.testFiles ?? []}
          id="test"
        />
      )}
      {!hasAICorrection && (
        <>
          <div className="col-12 col-sm-6 col-md-6 col-lg-6 col-xl-4">
            <span className=" font-bold text-muted font-weight-bold text-xs">
              {t('CREATE_QUESTION_CODE_TEST_CASES_TITLE')}
            </span>
          </div>
          {rowAnswer.map((row, index) => (
            <CodeAnswer
              {...row}
              onChange={(name, value) => handleOnChange(index, name, value)}
              onChangeOutput={(name, value) => handleOnChange(index, name, value)}
              index={index}
              onRemove={() => handleOnRemove(index)}
              disabled={readOnly}
            />
          ))}
          <button
            type="button"
            className=" bg-transparent border-0 text-xs text-blue-principal font-weight-600 mt-3"
            onClick={handleAddAswer}
            data-testid="question-code-create-option-btn"
            disabled={readOnly}
          >
            {t('CREATE_QUESTION_MULTIPLE_CHOICE_ADD_OPTION')}
          </button>
        </>
      )}
    </div>
  );
};

export default QuestionCode;
